"use client";

import { AnimatePresence, motion, type HTMLMotionProps } from "framer-motion";
import type { ComponentProps, ReactNode } from "react";
import { DURATION, EASE, SPRING, STAGGER } from "./tokens";
import { fadeUp, pop, staggerContainer } from "./variants";

type DivMotionProps = Omit<HTMLMotionProps<"div">, "children">;

interface FadeInProps extends DivMotionProps {
    children?: ReactNode;
    delay?: number;
    /** Vertical offset the element rises from; 0 for a pure fade. */
    y?: number;
    duration?: number;
}

/** Mount-only fade (+ optional rise). Extra props (className, data-*) are forwarded to the element. */
export const FadeIn = ({ children, delay = 0, y = 10, duration = DURATION.base, ...rest }: FadeInProps) => (
    <motion.div
        initial={{ opacity: 0, y }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration, ease: EASE.out, delay }}
        {...rest}
    >
        {children}
    </motion.div>
);

interface PopProps extends DivMotionProps {
    children?: ReactNode;
    delay?: number;
}

/** Mount-only scale-in with a springy settle, for icons and badges. */
export const Pop = ({ children, delay = 0, ...rest }: PopProps) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ ...SPRING.bouncy, delay }}
        {...rest}
    >
        {children}
    </motion.div>
);

interface StaggerProps extends DivMotionProps {
    children?: ReactNode;
    stagger?: number;
    delayChildren?: number;
}

/** Container that staggers its `StaggerItem` children on mount. */
export const Stagger = ({ children, stagger = STAGGER.items, delayChildren = 0, ...rest }: StaggerProps) => (
    <motion.div variants={staggerContainer(stagger, delayChildren)} initial="hidden" animate="show" {...rest}>
        {children}
    </motion.div>
);

interface StaggerItemProps extends DivMotionProps {
    children?: ReactNode;
    variant?: "fadeUp" | "pop";
    y?: number;
}

/** Child of `Stagger`: only declares variants, the parent drives the timing. */
export const StaggerItem = ({ children, variant = "fadeUp", y = 10, ...rest }: StaggerItemProps) => (
    <motion.div variants={variant === "pop" ? pop : fadeUp(y)} {...rest}>
        {children}
    </motion.div>
);

type PresenceProps = ComponentProps<typeof AnimatePresence>;

/** `AnimatePresence` that does not animate what is already on screen at mount. */
export const Presence = ({ initial = false, ...rest }: PresenceProps) => (
    <AnimatePresence initial={initial} {...rest} />
);
